import { Link } from "react-router-dom";
import {
  Printer, Download, ArrowLeft, ShoppingCart, Wrench, CheckCircle2,
  Layers, Truck, ClipboardCheck, Recycle, MapPin, DollarSign,
} from "lucide-react";
import { IMAGES, COMPANY } from "../lib/data";

// Clearly-marked placeholder — swap the text once specifics are confirmed.
const TBC = ({ children }) => (
  <span className="inline-block border border-dashed border-amber-500 bg-amber-50 text-amber-700 px-1.5 py-0.5 text-[8px] font-semibold uppercase tracking-wider rounded-sm">
    {children}
  </span>
);

const AVENUES = [
  {
    tag: "A",
    icon: ShoppingCart,
    label: "Own it",
    title: "Buy the Product",
    sub: "Any mat — you own & deploy",
    blurb:
      "Buy whatever mat makes the most sense for the job — any manufacturer, with the flagship Big Bad Mats when you want maximum durability and reuse. Own the fleet outright and deploy on your own schedule.",
    points: [
      "Procure any mat, any manufacturer — sourced to your spec",
      "Flagship Big Bad Mats for the longest life & lowest cost-per-use",
      "Own a reusable fleet on your books, deploy on your schedule",
      "Certified load ratings plus ramps, connectors & accessories",
      "Volume pricing that lowers cost-per-use with every reuse",
    ],
    best:
      "In-house crews with recurring site-access needs who want the lowest long-term cost and full control.",
  },
  {
    tag: "B",
    icon: Wrench,
    label: "Leave it to us",
    title: "Turnkey Service",
    sub: "Any mat — we handle it all",
    blurb:
      "Rent the right mat for your project — any type, any manufacturer, flagship Big Bad Mats — and let us handle everything: delivery, installation, maintenance, and removal. No capital purchase, no logistics burden.",
    points: [
      "Rent any mat, any manufacturer — flagship Big Bad Mats",
      "Site assessment & access plan built for your job",
      "Delivery, professional installation & on-project maintenance",
      "Teardown, removal & site restoration",
      "One accountable partner — a single point of contact",
    ],
    best:
      "One-off or short-duration projects, teams with no storage or handling capacity, or contractors delivering turnkey site access to their own end-clients.",
  },
];

const COMPARE = [
  { k: "Mat selection", a: "Any mat · flagship Big Bad Mats", b: "Any mat · flagship Big Bad Mats" },
  { k: "Capital", a: "One-time purchase (CapEx)", b: "No purchase — pay per project (OpEx)" },
  { k: "Ownership", a: "You own & keep the fleet", b: "We retain & manage the fleet" },
  { k: "Logistics", a: "You store, haul & deploy", b: "We handle everything, end to end" },
  { k: "Maintenance", a: "Your crew's responsibility", b: "Included in the service" },
  { k: "Best when", a: "Frequent, repeat use", b: "Project-based / no overhead" },
];

export default function GroundProtectionFlyer() {
  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      {/* top bar */}
      <div className="sticky top-0 z-40 bg-ink text-white border-b border-white/10 print:hidden">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/solutions" className="inline-flex items-center gap-2 text-white/70 hover:text-flame text-sm">
            <ArrowLeft className="h-4 w-4" /> Back to solutions
          </Link>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-[11px] uppercase tracking-widest text-white/50">Buy vs. Service · One-Page Flyer</span>
            <a href="/ground-protection-flyer.pdf" download data-testid="download-gp-flyer" className="inline-flex items-center gap-2 border border-white/25 text-white px-4 py-2 text-sm font-semibold hover:border-flame hover:text-flame transition-colors">
              <Download className="h-4 w-4" /> Download PDF
            </a>
            <button onClick={() => window.print()} data-testid="print-gp-flyer" className="inline-flex items-center gap-2 bg-flame text-white px-4 py-2 text-sm font-semibold hover:bg-flame/90 transition-colors">
              <Printer className="h-4 w-4" /> Print
            </button>
          </div>
        </div>
      </div>

      {/* ===== A4 LANDSCAPE SHEET ===== */}
      <div className="py-8 print:py-0 flex justify-center">
        <div
          data-testid="gp-flyer-sheet"
          className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col"
          style={{ fontSize: "10px" }}
        >
          {/* header */}
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[66%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVESTCO.</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">Ground Protection Solutions · Two Ways to Get Site-Ready</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[34px]">
                Own it, or leave it to us.
              </h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">
                We source or manage <span className="font-semibold text-ink">any mat, from any manufacturer</span> — with the flagship
                <span className="font-semibold text-ink"> Big Bad Mats</span> when durability matters most. Two clear paths:
                <span className="font-semibold text-ink"> buy the product</span> and run your own fleet, or
                <span className="font-semibold text-ink"> choose our full turnkey service</span> and we build, protect, and restore the site for you.
              </p>
            </div>
            <div className="w-[31%] flex flex-col items-end gap-2">
              <div className="w-full bg-ink text-white p-3 text-right">
                <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">Any mat · flagship Big Bad Mats</div>
                <div className="font-display font-extrabold text-flame text-[22px] leading-none mt-1">Buy or service</div>
                <div className="text-[8px] text-white/50 mt-1">Own your fleet · or full turnkey service</div>
              </div>
            </div>
          </header>

          {/* two avenues */}
          <div className="grid grid-cols-2 gap-4 pt-4">
            {AVENUES.map((av) => (
              <div key={av.tag} data-testid={`avenue-${av.tag}`} className="border border-border flex flex-col">
                <div className="bg-ink text-white px-4 py-3 flex items-center gap-3">
                  <span className="font-display font-black text-flame text-[26px] leading-none">{av.tag}</span>
                  <div className="flex-1">
                    <div className="text-[8px] uppercase tracking-[0.18em] text-flame">{av.label} · {av.sub}</div>
                    <div className="font-display font-bold leading-tight text-[16px]">{av.title}</div>
                  </div>
                  <av.icon className="h-7 w-7 text-white/25" />
                </div>
                <div className="p-4 flex-1 flex flex-col">
                  <p className="text-charcoal text-[10.5px] leading-snug">{av.blurb}</p>
                  <div className="mt-3 space-y-1.5">
                    {av.points.map((pt) => (
                      <div key={pt} className="flex items-start gap-2">
                        <CheckCircle2 className="h-3.5 w-3.5 text-flame flex-shrink-0 mt-[1px]" />
                        <span className="text-charcoal text-[10px] leading-snug">{pt}</span>
                      </div>
                    ))}
                  </div>
                  <div className="mt-3 pt-3 border-t border-border">
                    <div className="text-[8px] uppercase tracking-[0.18em] text-flame mb-1">Best for</div>
                    <p className="text-ink text-[10px] leading-snug font-medium">{av.best}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* decision helper + service facts */}
          <div className="grid grid-cols-3 gap-4 pt-4 flex-1 min-h-0">
            {/* comparison table */}
            <div className="col-span-2 border border-border overflow-hidden">
              <div className="bg-muted px-4 py-2 flex items-center justify-between border-b border-border">
                <span className="font-display font-bold text-ink text-[12px]">Which path is right for you?</span>
                <span className="text-[8px] uppercase tracking-[0.18em] text-muted-foreground">At a glance</span>
              </div>
              <table className="w-full border-collapse">
                <thead>
                  <tr className="text-[8px] uppercase tracking-[0.14em]">
                    <th className="text-left px-4 py-1.5 text-muted-foreground font-semibold w-[22%]"></th>
                    <th className="text-left px-3 py-1.5 text-ink font-bold border-l border-border">A · Buy the Product</th>
                    <th className="text-left px-3 py-1.5 text-flame font-bold border-l border-border">B · Turnkey Service</th>
                  </tr>
                </thead>
                <tbody>
                  {COMPARE.map((row, i) => (
                    <tr key={row.k} className={i % 2 ? "bg-muted/40" : ""}>
                      <td className="px-4 py-1.5 text-[9px] font-semibold uppercase tracking-wider text-charcoal">{row.k}</td>
                      <td className="px-3 py-1.5 text-[9.5px] text-charcoal border-l border-border leading-snug">{row.a}</td>
                      <td className="px-3 py-1.5 text-[9.5px] text-charcoal border-l border-border leading-snug">{row.b}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* service quick facts */}
            <div className="border border-flame/40 bg-flame/[0.03] p-4 flex flex-col">
              <div className="text-[8px] uppercase tracking-[0.18em] text-flame mb-2">Service business — quick facts</div>
              <div className="space-y-2.5">
                <div className="flex items-start gap-2">
                  <DollarSign className="h-4 w-4 text-flame flex-shrink-0" />
                  <div>
                    <div className="text-[9px] font-semibold text-ink">Pricing model</div>
                    <div className="text-[9px] text-charcoal leading-snug"><TBC>To confirm</TBC> e.g. per-project quote or rental + install.</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <ClipboardCheck className="h-4 w-4 text-flame flex-shrink-0" />
                  <div>
                    <div className="text-[9px] font-semibold text-ink">What's included</div>
                    <div className="text-[9px] text-charcoal leading-snug">Assessment · delivery · install · maintenance · removal.</div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="h-4 w-4 text-flame flex-shrink-0" />
                  <div>
                    <div className="text-[9px] font-semibold text-ink">Coverage & response</div>
                    <div className="text-[9px] text-charcoal leading-snug"><TBC>To confirm</TBC> service area & typical response time.</div>
                  </div>
                </div>
              </div>
              <div className="mt-auto pt-3 flex items-center gap-1.5 text-[8px] text-muted-foreground">
                <Layers className="h-3 w-3" /><Truck className="h-3 w-3" /><Recycle className="h-3 w-3" />
                <span>Delivered, installed & restored by OneHarvestCo.</span>
              </div>
            </div>
          </div>

          {/* footer */}
          <div className="mt-3 pt-2 border-t border-border flex items-center justify-between gap-4">
            <p className="text-[8px] text-muted-foreground leading-tight max-w-[62%]">
              Informational overview of OneHarvestCo. Ground Protection Solutions purchase and service options. Inclusions, pricing, coverage, and response times vary by scope and site conditions and are confirmed in your quote. Not a contract, quote, guarantee, or warranty of any specific result. © {new Date().getFullYear()} {COMPANY.legal}.
            </p>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <div className="font-display font-bold text-ink text-[12px] tracking-tight">Not sure which fits? We'll help you choose.</div>
                <div className="text-[9px] text-muted-foreground">{COMPANY.phone} · {COMPANY.email}</div>
              </div>
              <Link to="/request-quote" className="inline-flex items-center bg-flame text-white px-4 py-2.5 font-semibold text-[11px] hover:bg-flame/90 transition-colors print:hidden">
                Request a Quote
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
